<template>
    <div>
        <b-modal id="edit-plugin-modal" hide-footer size="md" centered title="Edit Plugin" @hidden="resetModal">
            <div class="modal_content">
                <b-alert dismissible :show="hasError" class="p-1" variant="danger" @dismissed="clearErrorMsg">
                    {{ errorMsg }}
                </b-alert>

                <!-- Title -->
                <p class="margin-for-title">Title*</p>
                <b-form-input class="mb-1" v-model="data.title" placeholder="Example: Drag And Drop" type="text" />

                <!-- Plugin Code -->
                <p class="margin-for-title">Code*</p>
                <b-form-input class="mb-1" v-model="data.pluginName" placeholder="Example: DragAndDrop" type="text" />

                <!-- Build File Upload -->
                <p class="margin-for-title">Build File</p>
                <b-form-file
                    v-model="data.buildFile"
                    placeholder="Choose ZIP file..."
                    drop-placeholder="Drop file here..."
                    accept=".zip"
                    class="mb-2"
                />
                <small class="text-muted">Leave empty to keep current build file</small>

                <div class="modal-footer mt-3">
                    <b-button @click="updatePlugin" :disabled="loading" variant="primary">
                        <b-spinner v-if="loading" small class="mr-1" />
                        {{ loading ? 'Updating...' : 'Update Plugin' }}
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import {
    BButton,
    BFormInput,
    BAlert,
    BSpinner,
    BFormFile,
} from "bootstrap-vue";
import axios from "axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
    components: {
        BButton,
        BFormInput,
        BAlert,
        BSpinner,
        BFormFile,
        ToastificationContent,
    },
    data() {
        return {
            errorMsg: "",
            loading: false,
            alertMessageToast: "",
            hasError: false,
            data: {
                title: "",
                pluginName: "",
                buildFile: null,
            },
        };
    },
    watch: {
        theme: function (newVal) {
            if (newVal) {
                this.data.title = this.theme.title || "";
                this.data.pluginName = this.theme.plugin || "";
                // buildFile is not pre-filled as it's a new upload
            }
        }
    },
    methods: {
     async updatePlugin() {
    // Validate your local form first
    let isValidated = this.validateData();
    if (!isValidated) return;

    this.loading = true;

    try {
        let response;

        // If there's a build file, use FormData
        if (this.data.buildFile) {
           const formData = new FormData();
formData.append('title', this.data.title || '');
formData.append('pluginName', this.data.pluginName || '');
formData.append('buildFile', this.data.buildFile);  // 👈 Tell Laravel this is a PUT


            response = await axios.post(
                `${process.env.VUE_APP_API_URL}/remote-plugin/edit/${this.theme._id}`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem("_t")}`,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
        } else {
            // No build file, send JSON
            const requestData = {
                title: this.data.title || '',
                pluginName: this.data.pluginName || '',
            };

            response = await axios.post(
                `${process.env.VUE_APP_API_URL}/remote-plugin/edit/${this.theme._id}`,
                requestData,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem("_t")}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
        }

        // Handle success
        if (response.data.success) {
            this.alertMessageToast = response.data.message || 'Plugin updated successfully';
            this.showToast("success");
            this.$emit('fetchPlugin');
            this.$bvModal.hide('edit-plugin-modal');
        }

    } catch (error) {
        // Handle validation error
        if (error.response && error.response.status === 422) {
            const errors = error.response.data.errors || {};
            this.alertMessageToast = error.response.data.message || 'Validation error';

            // Optional: show field errors individually
            if (errors.title) this.alertMessageToast += ` | Title: ${errors.title.join(', ')}`;
            if (errors.pluginName) this.alertMessageToast += ` | Plugin Name: ${errors.pluginName.join(', ')}`;
        } else if (error.response && error.response.data.message) {
            this.alertMessageToast = error.response.data.message;
        } else {
            this.alertMessageToast = 'An error occurred while updating plugin!';
        }
        this.showToast("danger");
    } finally {
        this.loading = false;
    }
},


        validateData() {
            this.errorMsg = '';
            this.hasError = false;
            
            const trimmedTitle = this.data.title && this.data.title.trim();
            if (!trimmedTitle) {
                this.errorMsg = 'Title cannot be empty or just spaces';
                this.hasError = true;
                return false;
            }
            this.data.title = trimmedTitle;

            const trimmedPluginName = this.data.pluginName && this.data.pluginName.trim();
            if (!trimmedPluginName) {
                this.errorMsg = 'Plugin code cannot be empty';
                this.hasError = true;
                return false;
            }

            if (trimmedPluginName.includes(' ')) {
                this.errorMsg = 'Spaces are not allowed in the plugin code';
                this.hasError = true;
                return false;
            }
            this.data.pluginName = trimmedPluginName;

            // Validate build file if provided
            if (this.data.buildFile) {
                if (this.data.buildFile.type !== 'application/zip' && 
                    this.data.buildFile.type !== 'application/x-zip-compressed') {
                    this.errorMsg = 'Please upload a valid ZIP file';
                    this.hasError = true;
                    return false;
                }

                // Check file size (10MB limit)
                const maxSize = 10 * 1024 * 1024; // 10MB in bytes
                if (this.data.buildFile.size > maxSize) {
                    this.errorMsg = 'File size should be less than 10MB';
                    this.hasError = true;
                    return false;
                }
            }

            return true;
        },

        showToast(variant) {
            this.$toast({
                component: ToastificationContent,
                props: {
                    title: "Notification",
                    icon: "BellIcon",
                    text: this.alertMessageToast,
                    variant,
                },
            });
        },
        
        clearErrorMsg() {
            this.errorMsg = "";
            this.hasError = false;
        },
        
        resetModal() {
            this.data = {
                title: "",
                pluginName: "",
                buildFile: null,
            };
            this.errorMsg = "";
            this.hasError = false;
            this.$emit('removeThemeData');
        },
    },
    props: ["theme"],
};
</script>

<style scoped>
.margin-for-title {
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.modal-footer {
    border-top: none;
    padding-top: 1rem;
}
</style>